// src/pages/payroll/DailyAttendanceEntry/DailyAttendanceEntryEdit.jsx
// Edit page (ASP-like) for Daily Attendance
// Uses Node APIs you already built:
// 1) GET  /api/payroll/daily-attendance-entry/edit-init?finYearId=XXXX&dailyId=EMPLOYEE_ID
// 2) POST /api/payroll/daily-attendance-entry/month-search?finYearId=XXXX   body: { dailyId, month, year }
// 3) PUT  /api/payroll/daily-attendance-entry/edit-submit?finYearId=XXXX   body: { dailyId, month, year, updates: [...] }
// 4) POST /api/payroll/daily-attendance-entry/delete?finYearId=XXXX        body: { dailyId, month, year, deleteIds: [...] }

import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ArrowLeft,
  Search,
  RefreshCcw,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp,
  Filter,
  Users,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const pad2 = (n) => String(n).padStart(2, "0");
const buildYearsList = () => {
  const current = new Date().getFullYear();
  const years = ["Select"];
  for (let y = 2010; y <= current; y++) years.push(String(y));
  return years;
};

const MONTHS = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS = [
  { key: "PRESENT", label: "Present" },
  { key: "HALF_PRESENT", label: "Half" },
  { key: "LEAVE", label: "Leave" },
  { key: "ABSENT", label: "Absent" },
  { key: "COMPULSORY_LOP", label: "Comp LOP" },
  { key: "HOLIDAY", label: "Holiday" },
  { key: "MGMT30", label: "Mgmt" },
  { key: "MGMT100", label: "Mgmt 100%" },
  { key: "CAMP", label: "Camp" },
];

function deriveStatusKeyFromRow(r) {
  // Node returns: fld_Others + booleans like fld_Present, fld_halfpresent, fld_Absent, fld_Compulsorylop, fld_monthlyLeave
  const others = String(r?.fld_Others ?? "");
  if (others === "H") return "HOLIDAY";
  if (others === "C") return "CAMP";
  if (others === "M") return "MGMT30";
  if (others === "M1") return "MGMT100";

  if (r?.fld_Present) return "PRESENT";
  if (r?.fld_HalfPresent || r?.fld_halfpresent) return "HALF_PRESENT";
  if (r?.fld_Absent) return "ABSENT";
  if (r?.fld_Compulsorylop) return "COMPULSORY_LOP";
  if (r?.fld_monthlyLeave) return "LEAVE";

  return "";
}

function safeStr(v) {
  return (v ?? "").toString().trim();
}

const DailyAttendanceEntryEdit = () => {
  const nav = useNavigate();
  const { empId } = useParams();
const employeeId = empId;   // then use employeeId everywhere below

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const userId = Number(localStorage.getItem("userId") || 0);
  const branchType = (localStorage.getItem("branchType") || "HO").toString().trim();
  const loginBranchId = (localStorage.getItem("branchId") || "").toString().trim();

  const apiHeaders = useMemo(() => {
    return {
      "x-loginuserid": String(userId || 0),
      "x-branchid": String(loginBranchId || ""),
      "x-branchtype": String(branchType || "HO"),
    };
  }, [userId, loginBranchId, branchType]);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-16 (EDIT)", []);

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // header info (employee)
  const [employee, setEmployee] = useState({ employeeId: 0, employeeCode: "", employeeName: "" });

  // filters: month/year like ASP
  const [month, setMonth] = useState("Select");
  const [year, setYear] = useState("Select");

  const years = useMemo(() => buildYearsList(), []);

  // grid rows (daily attendance for month)
  const [rows, setRows] = useState([]); // raw rows from API
  const [halfDayEnabled, setHalfDayEnabled] = useState(false);

  // selection + per row edit state
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // dailyAttendanceIds
  const [rowState, setRowState] = useState({}); // { [dailyAttendanceId]: { selected, statusKey, inTime, inAmPm, outTime, outAmPm, ot, dateText } }

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const successBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (loading ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (loading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const ensureFinYear = () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const normalizeRowStateFromRows = (list) => {
    const next = {};
    for (const r of list) {
      const id = Number(r?.fld_DailyAttendance_ID ?? 0);
      if (!id) continue;

      const statusKey = deriveStatusKeyFromRow(r);

      // Node query returns attendate (dd/MM/yyyy)
      const dateText = safeStr(r?.attendate || "");

      next[id] = {
        selected: false,
        statusKey: statusKey || "",
        inTime: safeStr(r?.fld_Intime || ""),
        inAmPm: safeStr(r?.fld_Intime_AMPM || "AM") || "AM",
        outTime: safeStr(r?.fld_Outtime || ""),
        outAmPm: safeStr(r?.fld_Outtime_AMPM || "PM") || "PM",
        ot: safeStr(r?.fld_OT ?? "0") || "0",
        dateText,
      };
    }
    setRowState(next);
    setSelectedIds([]);
    setSelectAll(false);
  };

  const loadEditInit = async () => {
    if (!ensureFinYear()) return;
    const empId = Number(employeeId || 0);
    if (!empId) return toast.error("Employee ID missing in URL");

    try {
      setLoading(true);
      console.log("[DAE-EDIT][INIT] employeeId:", empId, "finYearId:", finYearId);

      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance-entry/edit-init`, {
        params: { finYearId, dailyId: empId },
        headers: apiHeaders,
      });

      console.log("[DAE-EDIT][INIT] response:", res.data);

      if (res?.data?.success) {
        setEmployee(res.data.employee || { employeeId: empId, employeeCode: "", employeeName: "" });

        // default month/year from server
        const m = safeStr(res.data.month || "Select");
        const y = safeStr(res.data.year || "Select");

        setMonth(MONTHS.includes(m) ? m : "Select");
        setYear(y || "Select");

        setRows(res.data.rows || []);
        setHalfDayEnabled(!!res.data.halfDayEnabled);
        normalizeRowStateFromRows(res.data.rows || []);
      } else {
        toast.error(res?.data?.message || "Edit init failed");
      }
    } catch (err) {
      console.error("[DAE-EDIT][INIT] ERROR:", err);
      toast.error(err?.response?.data?.message || err?.message || "Edit init failed");
    } finally {
      setLoading(false);
    }
  };

  const monthSearch = async () => {
    if (!ensureFinYear()) return;
    const empId = Number(employeeId || 0);
    if (!empId) return toast.error("Employee ID missing in URL");
    if (!month || month === "Select") return toast.warn("Please select Month");
    if (!year || year === "Select") return toast.warn("Please select Year");

    try {
      setLoading(true);
      console.log("[DAE-EDIT][MONTH-SEARCH] body:", { dailyId: empId, month, year });

      const res = await axios.post(
        `${BASE_URL}/api/payroll/daily-attendance-entry/month-search`,
        { dailyId: empId, month, year },
        { params: { finYearId }, headers: apiHeaders }
      );

      console.log("[DAE-EDIT][MONTH-SEARCH] response:", res.data);

      if (res?.data?.success) {
        const list = res.data.rows || [];
        setRows(list);
        setHalfDayEnabled(!!res.data.halfDayEnabled);
        normalizeRowStateFromRows(list);
        toast.success(`Loaded ${list.length} day(s)`);
      } else {
        setRows([]);
        normalizeRowStateFromRows([]);
        toast.error(res?.data?.message || "Month search failed");
      }
    } catch (err) {
      console.error("[DAE-EDIT][MONTH-SEARCH] ERROR:", err);
      toast.error(err?.response?.data?.message || err?.message || "Month search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (dailyAttendanceId) => {
    setRowState((prev) => {
      const cur = prev[dailyAttendanceId] || {};
      const nextSel = !cur.selected;
      return { ...prev, [dailyAttendanceId]: { ...cur, selected: nextSel } };
    });

    setSelectedIds((prev) =>
      prev.includes(dailyAttendanceId) ? prev.filter((x) => x !== dailyAttendanceId) : [...prev, dailyAttendanceId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedIds([]);
      setRowState((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => (next[k] = { ...next[k], selected: false }));
        return next;
      });
      return;
    }

    const ids = rows
      .map((r) => Number(r?.fld_DailyAttendance_ID ?? 0))
      .filter(Boolean);

    setSelectAll(true);
    setSelectedIds(ids);
    setRowState((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = { ...(next[id] || {}), selected: true };
      });
      return next;
    });
  };

  const updateRowField = (dailyAttendanceId, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [dailyAttendanceId]: { ...(prev[dailyAttendanceId] || {}), [field]: value },
    }));
  };

  const setOneStatus = (dailyAttendanceId, statusKey) => {
    if (!halfDayEnabled && statusKey === "HALF_PRESENT") {
      toast.warn("Half-day disabled for this branch");
      // still allow if you want; if you want hard block, uncomment:
      // return;
    }

    setRowState((prev) => ({
      ...prev,
      [dailyAttendanceId]: { ...(prev[dailyAttendanceId] || {}), statusKey },
    }));
  };

  const validateBeforeUpdate = () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select atleast one");
      return false;
    }
    const missing = selectedIds.filter((id) => !safeStr(rowState?.[id]?.statusKey));
    if (missing.length > 0) {
      toast.warn("Please select status for all selected rows");
      return false;
    }
    return true;
  };

  const submitEditUpdates = async () => {
    if (!ensureFinYear()) return;
    if (!validateBeforeUpdate()) return;

    const empId = Number(employeeId || 0);
    if (!empId) return toast.error("Employee ID missing in URL");

    try {
      setLoading(true);

      const updates = selectedIds.map((id) => {
        const st = rowState?.[id] || {};
        return {
          dailyAttendanceId: Number(id),
          dateText: safeStr(st.dateText), // dd/MM/yyyy (from API)
          inTime: safeStr(st.inTime),
          inAmPm: safeStr(st.inAmPm || "AM") || "AM",
          outTime: safeStr(st.outTime),
          outAmPm: safeStr(st.outAmPm || "PM") || "PM",
          ot: safeStr(st.ot ?? "0") || "0",
          statusKey: safeStr(st.statusKey),
        };
      });

      console.log("[DAE-EDIT][SUBMIT] payload:", { dailyId: empId, month, year, updatesCount: updates.length, updates });

      const res = await axios.put(
        `${BASE_URL}/api/payroll/daily-attendance-entry/edit-submit`,
        { dailyId: empId, month, year, updates },
        { params: { finYearId }, headers: apiHeaders }
      );

      console.log("[DAE-EDIT][SUBMIT] response:", res.data);

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Updated Successfully..");
        // reload month grid
        await monthSearch();
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("[DAE-EDIT][SUBMIT] ERROR:", err);
      toast.error(err?.response?.data?.message || err?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    if (!ensureFinYear()) return;
    const empId = Number(employeeId || 0);
    if (!empId) return toast.error("Employee ID missing in URL");
    if (selectedIds.length === 0) return toast.warn("Please select atleast one");

    const ok = window.confirm(`Delete ${selectedIds.length} record(s)?`);
    if (!ok) return;

    try {
      setLoading(true);

      console.log("[DAE-EDIT][DELETE] payload:", { dailyId: empId, month, year, deleteIds: selectedIds });

      const res = await axios.post(
        `${BASE_URL}/api/payroll/daily-attendance-entry/delete`,
        { dailyId: empId, month, year, deleteIds: selectedIds },
        { params: { finYearId }, headers: apiHeaders }
      );

      console.log("[DAE-EDIT][DELETE] response:", res.data);

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted Successfully..");
        await monthSearch();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("[DAE-EDIT][DELETE] ERROR:", err);
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEditInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE EDIT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* header */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Daily Attendance Edit
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
  Employee:&nbsp;
  <span className={`font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
    {employee.employeeName || "-"}
  </span>
  <span className={`ml-2 px-3 py-1 rounded-full text-xs font-extrabold ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"}`}>
    {employee.employeeCode || "-"}
  </span>
  <span className="mx-2 opacity-60">•</span>
  Select rows, edit, then Update/Delete.
</div>

              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Days
              </div>

              {halfDayEnabled && (
                <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"}`}>
                  Half-Day Enabled
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => nav("/payroll/DailyAttendanceListing")} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back to Listing
              </button>

              <button onClick={loadEditInit} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reload
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* filters */}
          {showFilters && (
          <div className={`${cardBase} p-6 mb-6`}>
  {/* Header */}
  <div className="flex items-center gap-2 mb-5">
    <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
    <div className="text-lg font-extrabold">Month / Year</div>
  </div>

  {/* ROW 1: Month / Year / Search */}
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
    {/* Month */}
    <div className="lg:col-span-4">
      <label className={labelBase}>Month</label>
      <select
        className={`${inputBase} w-full`}
        value={month}
        onChange={(e) => setMonth(e.target.value)}
      >
        {MONTHS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>

    {/* Year */}
    <div className="lg:col-span-3">
      <label className={labelBase}>Year</label>
      <select
        className={`${inputBase} w-full`}
        value={year}
        onChange={(e) => setYear(e.target.value)}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>

    {/* Search Button */}
    <div className="lg:col-span-5 lg:justify-self-end">
      <button onClick={monthSearch} className={primaryBtn} disabled={loading}>
        <Search className="w-4 h-4" />
        Search Month
      </button>
    </div>
  </div>

  {/* Employee display (FULL WIDTH, clean) */}
  <div className="mt-4">
    <label className={labelBase}>Employee</label>
    <div
      className={`w-full px-4 py-3 rounded-2xl border flex items-center justify-between gap-4 ${
        isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="min-w-0">
        <div className={`font-extrabold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
          {employee?.employeeName || "-"}
        </div>
        <div className={`text-xs font-bold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {employee?.employeeCode || "-"}
        </div>
      </div>

      <div
        className={`shrink-0 text-xs font-extrabold px-3 py-1 rounded-full ${
          isDark ? "bg-white/10 text-white" : "bg-white text-gray-800 border border-gray-200"
        }`}
      >
        ID: {employee?.employeeId || "-"}
      </div>
    </div>
  </div>

  {/* ROW 2: Selected + Update + Delete (below) */}
  <div className="mt-4 flex flex-wrap gap-3 items-center justify-start lg:justify-end">
    <div
      className={`px-4 py-3 rounded-2xl text-sm font-extrabold whitespace-nowrap ${
        isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
      }`}
    >
      Selected: {selectedIds.length}
    </div>

    <button
      onClick={submitEditUpdates}
      className={successBtn}
      disabled={loading || selectedIds.length === 0}
    >
      <Save className="w-4 h-4" />
      Update
    </button>

    <button
      onClick={deleteSelected}
      className={dangerBtn}
      disabled={loading || selectedIds.length === 0}
    >
      <Trash2 className="w-4 h-4" />
      Delete
    </button>
  </div>

  {/* Tip */}
  <div className="mt-4 text-sm opacity-70">
    Tip: Select checkbox → edit Status/Times/OT → click <b>Update Selected</b>. For delete, select and click{" "}
    <b>Delete Selected</b>.
  </div>
</div>


          )}

          {/* table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-bold">S.No</th>
                    <th className="px-3 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                    <th className="px-3 py-4 text-center font-bold">Date</th>
                    <th className="px-3 py-4 text-center font-bold">Status</th>
                    <th className="px-3 py-4 text-center font-bold">In</th>
                    <th className="px-3 py-4 text-center font-bold">Out</th>
                    <th className="px-3 py-4 text-center font-bold">OT</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 10 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-3 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const dailyAttendanceId = Number(r?.fld_DailyAttendance_ID ?? 0);
                      const st = rowState?.[dailyAttendanceId] || {};
                      const disabled = !st.selected;

                      const dateText = safeStr(st.dateText || r?.attendate || "");

                      return (
                        <tr
                          key={dailyAttendanceId || idx}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>

                          <td className="px-3 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={!!st.selected}
                              onChange={() => toggleSelect(dailyAttendanceId)}
                              className="w-4 h-4"
                            />
                          </td>

                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {dateText || "-"}
                          </td>

                          <td className="px-3 py-4 text-center">
                            <select
                              className={`${inputBase} py-2`}
                              value={st.statusKey || ""}
                              onChange={(e) => setOneStatus(dailyAttendanceId, e.target.value)}
                              disabled={disabled}
                            >
                              <option value="">Select</option>
                              {STATUS.map((s) => (
                                <option key={s.key} value={s.key}>
                                  {s.label}
                                </option>
                              ))}
                            </select>

                            {!halfDayEnabled && st.statusKey === "HALF_PRESENT" && (
                              <div className="text-xs mt-1 text-red-400 font-bold">Half-day disabled for this branch</div>
                            )}
                          </td>

                          <td className="px-3 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <input
                                className={`${inputBase} py-2 w-24`}
                                value={st.inTime || ""}
                                onChange={(e) => updateRowField(dailyAttendanceId, "inTime", e.target.value)}
                                placeholder="hh:mm"
                                disabled={disabled}
                              />
                              <select
                                className={`${inputBase} py-2 w-20`}
                                value={st.inAmPm || "AM"}
                                onChange={(e) => updateRowField(dailyAttendanceId, "inAmPm", e.target.value)}
                                disabled={disabled}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </td>

                          <td className="px-3 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <input
                                className={`${inputBase} py-2 w-24`}
                                value={st.outTime || ""}
                                onChange={(e) => updateRowField(dailyAttendanceId, "outTime", e.target.value)}
                                placeholder="hh:mm"
                                disabled={disabled}
                              />
                              <select
                                className={`${inputBase} py-2 w-20`}
                                value={st.outAmPm || "PM"}
                                onChange={(e) => updateRowField(dailyAttendanceId, "outAmPm", e.target.value)}
                                disabled={disabled}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </td>

                          <td className="px-3 py-4 text-center">
                            <input
                              className={`${inputBase} py-2 w-20 text-center`}
                              value={String(st.ot ?? "0")}
                              onChange={(e) => updateRowField(dailyAttendanceId, "ot", e.target.value)}
                              disabled={disabled}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 text-sm opacity-70">
            Note (ASP rule): If Monthly Attendance exists for that month/year, Node will block edit/delete.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DailyAttendanceEntryEdit;
